import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { GetRequest } from "../../../../api/Request";
import { StoreWithAnalytics } from "../storeAnalytics.types";
import { StoreSalesChannelEnum, SubscriptionTypeEnum } from "../../../../enum";
import { formatDate } from "../../../../util/util";
import "../styles/storeAnalyticsList.scss";

const StoreAnalyticsList: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<StoreWithAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(20);
  const [name, setName] = useState<string>("");
  const [subscriptionType, setSubscriptionType] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [minActiveDays, setMinActiveDays] = useState<number | undefined>();
  const [inactiveDaysGreaterThan, setInactiveDaysGreaterThan] = useState<number | undefined>();

  const fetchStoreList = useCallback(async () => {
    setLoading(true);
    let url = `admin/v1/store/analytics?page=${pageNumber}&limit=${limit}`;
    if (name) url += `&name=${encodeURIComponent(name)}`;
    if (subscriptionType) url += `&subscription-type=${subscriptionType}`;
    if (status) url += `&status=${status}`;
    if (minActiveDays) url += `&min-active-days=${minActiveDays}`;
    if (inactiveDaysGreaterThan) url += `&inactive-days-greater-than=${inactiveDaysGreaterThan}`;

    await GetRequest(url)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || "Failed to fetch store analytics");
        setLoading(false);
      });
  }, [pageNumber, limit, name, subscriptionType, status, minActiveDays, inactiveDaysGreaterThan]);

  // Debounce name search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchStoreList();
    }, 500); // 500ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [name]);

  // Fetch immediately when other filters change
  useEffect(() => {
    fetchStoreList();
  }, [pageNumber, limit, subscriptionType, status, minActiveDays, inactiveDaysGreaterThan]);

  const handleApplyFilters = () => {
    setPageNumber(1);
    fetchStoreList();
  };

  const handleResetFilters = () => {
    setName("");
    setSubscriptionType("");
    setStatus("");
    setMinActiveDays(undefined);
    setInactiveDaysGreaterThan(undefined);
    setLimit(20);
    setPageNumber(1);
  };

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
  };

  const handleViewStore = (storeId: string) => {
    navigate(`/store/${storeId}/analytics`);
  };

  const handleUpdateSubscription = (storeId: string) => {
    navigate(`/store/${storeId}/subscription`);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  if (loading && data.length === 0) {
    return (
      <div className="storeAnalyticsList">
        <div className="storeAnalyticsListLoading">Loading stores...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="storeAnalyticsList">
        <div className="storeAnalyticsListError">{error}</div>
      </div>
    );
  }

  return (
    <div className="storeAnalyticsList">
      <div className="storeAnalyticsListHeader">
        <h1>Store Analytics List</h1>
        <p>Manage and monitor store subscriptions and performance</p>
      </div>

      <div className="storeAnalyticsListFilters">
        <div className="filtersRow">
          <div className="filterGroup">
            <label>Store Name</label>
            <input
              type="text"
              placeholder="Search by name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="filterGroup">
            <label>Subscription Type</label>
            <select
              value={subscriptionType}
              onChange={(e) => setSubscriptionType(e.target.value)}
            >
              <option value="">All</option>
              <option value={SubscriptionTypeEnum.Paid}>Paid</option>
              <option value={SubscriptionTypeEnum.Free}>Free</option>
              <option value={SubscriptionTypeEnum.Trial}>Trial</option>
            </select>
          </div>

          <div className="filterGroup">
            <label>Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>

          <div className="filterGroup">
            <label>Min Active Days</label>
            <input
              type="number"
              placeholder="e.g., 10"
              value={minActiveDays || ""}
              onChange={(e) =>
                setMinActiveDays(e.target.value ? parseInt(e.target.value) : undefined)
              }
            />
          </div>

          <div className="filterGroup">
            <label>Inactive Days Greater Than</label>
            <input
              type="number"
              placeholder="e.g., 30"
              value={inactiveDaysGreaterThan || ""}
              onChange={(e) =>
                setInactiveDaysGreaterThan(e.target.value ? parseInt(e.target.value) : undefined)
              }
            />
          </div>

          <div className="filterGroup">
            <label>Items Per Page</label>
            <select
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value))}
            >
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
        </div>

        <div className="filterActions">
          <button className="applyBtn" onClick={handleApplyFilters}>
            Apply Filters
          </button>
          <button className="resetBtn" onClick={handleResetFilters}>
            Reset
          </button>
        </div>
      </div>

      {data.length > 0 ? (
        <>
          <div className="storeAnalyticsListTableContainer">
            <table className="storeAnalyticsListTable">
              <thead>
                <tr>
                  <th>Store Name</th>
                  <th>Phone</th>
                  <th>Subscription</th>
                  <th>Plan</th>
                  <th>Expiry</th>
                  <th>Status</th>
                  <th>Active Days</th>
                  <th>Total Sales</th>
                  <th>Total Bills</th>
                  <th>Last 7 Days Sales</th>
                  <th>Channel</th>
                  <th>Online Orders</th>
                  <th>Created On</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((store: StoreWithAnalytics) => (
                  <tr key={store._id}>
                    <td className="storeName">{store.name}</td>
                    <td>
                      {store.phoneNumberList?.[0]?.phoneNumber || "N/A"}
                    </td>
                    <td>
                      {store.subscription ? (
                        <span
                          className={`storeAnalyticsListBadge storeAnalyticsListBadge${store.subscription.type.charAt(0).toUpperCase() + store.subscription.type.slice(1).toLowerCase()}`}
                        >
                          {store.subscription.type}
                        </span>
                      ) : (
                        <span className="storeAnalyticsListBadge storeAnalyticsListBadgeFree">
                          Free
                        </span>
                      )}
                    </td>
                    <td>{store.subscription?.plan || "N/A"}</td>
                    <td>{store.subscription?.expiryDate ? formatDate(store.subscription.expiryDate) : "N/A"}</td>
                    <td>
                      {store.subscription ? (
                        <span
                          className={`storeAnalyticsListBadge storeAnalyticsListBadge${
                            store.isExpired ? "Expired" : "Active"
                          }`}
                        >
                          {store.isExpired ? "Expired" : "Active"}
                        </span>
                      ) : (
                        <span className="storeAnalyticsListBadge storeAnalyticsListBadgeActive">
                          Active
                        </span>
                      )}
                    </td>
                    <td>{store.analytics.totalActiveDays}</td>
                    <td>{formatCurrency(store.analytics.totalSales)}</td>
                    <td>{store.analytics.totalBills}</td>
                    <td>{formatCurrency(store.analytics.last7DaysSales)}</td>
                    <td>
                      <span
                        className={`storeAnalyticsListBadge storeAnalyticsListBadge${
                          store.salesChannel === StoreSalesChannelEnum.Both ? "Both" : "Offline"
                        }`}
                      >
                        {store.salesChannel === StoreSalesChannelEnum.Both
                          ? "Offline + Online"
                          : "Offline"}
                      </span>
                    </td>
                    <td>
                      {store.analytics.onlineOrderCount ?? 0}
                      <div className="storeAnalyticsListSubText">
                        {store.analytics.last30DaysOnlineOrderCount ?? 0} in last 30 days
                      </div>
                    </td>
                    <td>{store.createdAt ? formatDate(store.createdAt) : "N/A"}</td>
                    <td>
                      <div className="storeAnalyticsListActions">
                        <button
                          className="viewBtn"
                          onClick={() => handleViewStore(store._id)}
                        >
                          View
                        </button>
                        <button
                          className="updateBtn"
                          onClick={() => handleUpdateSubscription(store._id)}
                        >
                          Update
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="storeAnalyticsListPagination">
            <div className="paginationInfo">
              Showing {(pageNumber - 1) * limit + 1} to{" "}
              {Math.min(pageNumber * limit, data.length)}{" "}
              of {data.length} stores
            </div>
            <div className="paginationControls">
              <button
                onClick={() => handlePageChange(pageNumber - 1)}
                disabled={pageNumber === 1}
              >
                Previous
              </button>
              <span className="pageNumber">
                Page {pageNumber}
              </span>
              <button
                onClick={() => handlePageChange(pageNumber + 1)}
                disabled={data.length < limit}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="storeAnalyticsListEmpty">
          No stores found matching the criteria.
        </div>
      )}
    </div>
  );
};

export default StoreAnalyticsList;
